// DispatcherConsole.tsx — Motorola-style layout revamp
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Icon } from '@iconify/react';
import { useRadio } from '../../contexts/RadioContext';
import './DispatcherConsole.css';

type Talkgroup = {
  id: string;
  name: string;
  isAllCall?: boolean;
};

interface TileProps {
  talkgroup: Talkgroup;
  isActive: boolean;
  isSelected: boolean;
  isReceiving: boolean;
  isBusy: boolean;
  onSelect: () => void;
  onToggleMute: () => void;
  onVolumeChange: (v: number) => void;
  isMuted: boolean;
  volume: number;
  isPinned: boolean;
  onTogglePin: () => void;
  hasActiveConsumer: boolean;
  transmissionLogs: any[];
  playTransmissionAudio: (logId: string) => void;
  connectedUsers: Map<string, any>;
}

const TalkgroupTile: React.FC<TileProps> = ({
  talkgroup,
  isActive,
  isSelected,
  isReceiving,
  isBusy,
  onSelect,
  onToggleMute,
  onVolumeChange,
  isMuted,
  volume,
  isPinned,
  onTogglePin,
  hasActiveConsumer,
  transmissionLogs,
  playTransmissionAudio,
  connectedUsers
}) => {
  const [showLogs, setShowLogs] = useState(false);
  const [currentTime, setCurrentTime] = useState(Date.now());
  
  // Helper function to get proper unit display name with claimed unit info
  const getUnitDisplayName = (log: any) => {
    // Find the user in connectedUsers to get claimed unit info
    const user = connectedUsers.get(log.userId);
    if (user && user.role === 'dispatcher') {
      return 'DISPATCHER';
    }
    if (user && user.claimedUnit) {
      return user.claimedUnit;
    }
    // Fallback to displayName or userId
    return log.displayName || log.userId;
  };

  // Helper function to check if user is a dispatcher
  const isDispatcher = (log: any) => {
    const user = connectedUsers.get(log.userId);
    return user && user.role === 'dispatcher';
  };

  // Helper function to get username for display
  const getUsername = (log: any) => {
    // Find the user in connectedUsers to get display name
    const user = connectedUsers.get(log.userId);
    if (user && user.displayName) {
      return user.displayName;
    }
    // Fallback to log displayName or userId
    return log.displayName || log.userId;
  };

  // Helper function to format duration in M:SS format
  const formatDuration = (log: any) => {
    if (log.duration) {
      const totalSeconds = Math.round(log.duration / 1000);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    } else if (log.endTime) {
      const totalSeconds = Math.round((log.endTime - log.startTime) / 1000);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    } else {
      return 'Active';
    }
  };

  // Update current time every second for accurate timers
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Get dispatcher transmission state from parent component
  const { isTransmitting, currentTalkgroup } = useRadio();
  
  // TX lamp should light up when dispatcher is transmitting on this talkgroup
  const isDispatcherTransmitting = isTransmitting && (
    currentTalkgroup === talkgroup.id || 
    currentTalkgroup === 'ALL_CALL'
  );
  return (
    <div
      className={[
        'tg-tile',
        isSelected && 'is-selected',
        isActive && 'is-active',
        isBusy && 'is-busy',
        isPinned && 'is-pinned',
        hasActiveConsumer && 'has-consumer',
        isMuted && 'is-muted',
      ].filter(Boolean).join(' ')}
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => ((e.key === 'Enter' || e.key === ' ') && onSelect())}
      aria-pressed={isSelected}
      aria-label={`Talkgroup ${talkgroup.name}${isMuted ? ' muted' : ''}`}
    >
      <div className="tg-head">
        <div className="tg-name" title={talkgroup.name}>{talkgroup.name}</div>
        <div className="tg-header-controls">
          <div className="tg-status-lamps">
            <div className={['status-lamp', 'rx-lamp', isReceiving ? 'on' : 'off'].join(' ')}>
              <span>RX</span>
            </div>
            <div className={['status-lamp', 'tx-lamp', isDispatcherTransmitting ? 'on' : 'off'].join(' ')}>
              <span>TX</span>
            </div>
          </div>
          <div className="tg-vol">
            <input
              type="range"
              min={0}
              max={100}
              value={volume}
              onChange={(e) => onVolumeChange(parseInt(e.target.value))}
              onClick={(e) => e.stopPropagation()}
              aria-label="Volume"
            />
          </div>
          <div className="tg-actions">
            <button
              type="button"
              className={['icon-btn', isPinned && 'on'].filter(Boolean).join(' ')}
              onClick={(e) => { e.stopPropagation(); onTogglePin(); }}
              aria-label={isPinned ? 'Unpin talkgroup' : 'Pin talkgroup'}
              title={isPinned ? 'Unpin' : 'Pin'}
            ><Icon icon="material-symbols:push-pin" width="18" height="18" /></button>
            {transmissionLogs.length > 0 && (
              <button
                type="button"
                className="icon-btn history-btn"
                onClick={(e) => { e.stopPropagation(); setShowLogs(true); }}
                aria-label="View transmission history"
                title="View History"
              ><Icon icon="material-symbols:history" width="18" height="18" /></button>
            )}
            <button
              type="button"
              className={['icon-btn', isMuted && 'on'].filter(Boolean).join(' ')}
              onClick={(e) => { 
                e.stopPropagation(); 
                console.log(`[TalkgroupTile] Mute button clicked for ${talkgroup.id}`);
                onToggleMute(); 
              }}
              aria-label={isMuted ? 'Unmute talkgroup' : 'Mute talkgroup'}
              title={isMuted ? 'Unmute' : 'Mute'}
            >{isMuted ? <Icon icon="material-symbols:volume-off" width="18" height="18" /> : <Icon icon="material-symbols:volume-up" width="18" height="18" />}</button>
          </div>
        </div>
      </div>


      <div className="tg-activity">
        {transmissionLogs.length > 0 ? (
          <div className="activity-logs">
            {transmissionLogs.slice(-3).reverse().map((log, index) => (
              <div key={log.id || `log-${index}`} className="activity-entry">
                <div className="activity-row">
                  <div className="activity-left">
                    <span className="activity-dot"></span>
                    <span className={`activity-type ${isDispatcher(log) ? 'dispatcher' : 'unit'}`}>
                      {getUnitDisplayName(log)}
                    </span>
                    <span className="activity-author">{getUsername(log)}</span>
                  </div>
                  <div className="activity-right">
                    <span className="activity-time">
                      {new Date(log.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                    </span>
                    <span className="activity-duration">
                      {formatDuration(log)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="no-activity">No recent activity</div>
        )}
      </div>

      {/* Log History Popout */}
      {showLogs && (
        <div className="log-popout-overlay" onClick={() => setShowLogs(false)}>
          <div className="log-popout" onClick={(e) => e.stopPropagation()}>
            <div className="log-popout-header">
              <h3>{talkgroup.name} - Transmission History</h3>
              <button className="close-btn" onClick={() => setShowLogs(false)}><Icon icon="material-symbols:close" width="20" height="20" /></button>
            </div>
            <div className="log-popout-content">
              {transmissionLogs
                .filter(log => {
                  const twentyMinutesAgo = currentTime - (20 * 60 * 1000);
                  return log.startTime >= twentyMinutesAgo;
                })
                .slice()
                .reverse()
                .map((log, index) => (
                <div key={log.id || `log-${index}`} className="log-popout-entry">
                  <div className="log-popout-header-row">
                    <span className="log-popout-author">{getUsername(log)}</span>
                    <span className={`log-popout-type ${isDispatcher(log) ? 'dispatcher' : 'unit'}`}>
                      {getUnitDisplayName(log)}
                    </span>
                  </div>
                  <div className="log-popout-details">
                    <span className="log-popout-duration">
                      {formatDuration(log)}
                    </span>
                    <span className="log-popout-time">
                      {new Date(log.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                    </span>
                  </div>
                  {log.audioUrl && (
                    <div className="log-popout-actions">
                      <button 
                        className="playback-btn"
                        onClick={() => playTransmissionAudio(log.id)}
                        title="Play transmission"
                      >
                        <Icon icon="material-symbols:play-arrow" width="18" height="18" style={{ verticalAlign: 'middle', marginRight: '4px' }} /> Play
                      </button>
                    </div>
                  )}
                  {!log.audioUrl && (
                    <div className="log-popout-actions">
                      <span style={{ fontSize: '12px', color: '#666' }}>No audio</span>
                    </div>
                  )}
                </div>
              ))}
              {transmissionLogs.filter(log => {
                const twentyMinutesAgo = currentTime - (20 * 60 * 1000);
                return log.startTime >= twentyMinutesAgo;
              }).length === 0 && (
                <div className="log-popout-empty">No transmission history in the last 20 minutes</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const DispatcherConsole: React.FC = () => {
  const {
    user,
    talkgroups,
    activeTalkgroups,
    transmissionLogs,
    connectedUsers,
    userTalkgroupAssignments,
    isTransmitting,
    devices,
    selectedInputDevice,
    selectedOutputDevice,
    isInitialized,
    isConnected,
    startTransmitting,
    stopTransmitting,
    subscribeToTalkgroup,
    updateDevice,
    testAudioPlayback,
    setTalkgroupVolume,
    setTalkgroupMuted,
    txFxEnabled,
    setTxFxEnabled,
    p25VocoderConfig,
    setP25VocoderMode,
    hasConsumerForTalkgroup,
    playTransmissionAudio,
    moveUserToTalkgroup,
    refreshConnectedUsers,
    talkgroupVolumes,
    mutedTalkgroups,
    getUnitStatusColor,
  } = useRadio() as any;

  const [selectedTalkgroup, setSelectedTalkgroup] = useState<string | null>(null);
  const [pinnedTalkgroups, setPinnedTalkgroups] = useState<Set<string>>(new Set());
  const [userLastActive, setUserLastActive] = useState<Map<string, number>>(new Map());
  const [isPTTPressed, setIsPTTPressed] = useState(false);
  const [isPreemptMode, setIsPreemptMode] = useState(false);
  const [networkStatus, setNetworkStatus] = useState({ latency: 0, packetLoss: 0 });
  const [serverStatus, setServerStatus] = useState<'connected'|'disconnected'|'reconnecting'>('disconnected');
  const [showSettings, setShowSettings] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  const pttButtonRef = useRef<HTMLButtonElement>(null);
  const subscribedTalkgroupsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (isInitialized && talkgroups.length > 0) {
      talkgroups.forEach((tg: Talkgroup) => {
        if (!subscribedTalkgroupsRef.current.has(tg.id)) {
          subscribedTalkgroupsRef.current.add(tg.id);
          subscribeToTalkgroup(tg.id);
        }
      });
    }
  }, [talkgroups, isInitialized, subscribeToTalkgroup]);

  useEffect(() => {
    if (talkgroups.length > 0 && !selectedTalkgroup) {
      const primary = talkgroups.find((tg: Talkgroup) => tg.id === 'PRIMARY');
      setSelectedTalkgroup(primary ? 'PRIMARY' : talkgroups[0].id);
    }
  }, [talkgroups, selectedTalkgroup]);

  useEffect(() => {
    const release = () => { if (isPTTPressed) handlePTTRelease(); };
    window.addEventListener('mouseup', release);
    window.addEventListener('touchend', release, { passive: false });
    window.addEventListener('touchcancel', release, { passive: false });
    return () => {
      window.removeEventListener('mouseup', release);
      window.removeEventListener('touchend', release);
      window.removeEventListener('touchcancel', release);
    };
  }, [isPTTPressed]);

  useEffect(() => {
    if (isConnected) {
      setServerStatus('connected');
      // Refresh connected users when connected (one-time)
      refreshConnectedUsers();
      
      const id = setInterval(() => {
        setNetworkStatus({
          latency: Math.floor(Math.random() * 50) + 40,
          packetLoss: Math.random() * 1.0,
        });
        // Removed automatic user refresh - now handled by WebSocket events
      }, 2000);
      return () => clearInterval(id);
    } else {
      setServerStatus('disconnected');
    }
  }, [isConnected, refreshConnectedUsers]);

  // Update current time every second
  useEffect(() => {
    const timeInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timeInterval);
  }, []);

  // Track transmission activity to update last active times
  useEffect(() => {
    const updateLastActive = (userId: string) => {
      setUserLastActive(prev => {
        const newMap = new Map(prev);
        newMap.set(userId, Date.now());
        return newMap;
      });
    };

    // Listen for transmission logs to track activity
    const checkTransmissionActivity = () => {
      transmissionLogs.forEach((logs: any[], _talkgroupId: string) => {
        logs.forEach((log: any) => {
          if (log.userId && log.startTime) {
            const lastActive = userLastActive.get(log.userId);
            if (!lastActive || log.startTime > lastActive) {
              updateLastActive(log.userId);
            }
          }
        });
      });
    };

    checkTransmissionActivity();
  }, [transmissionLogs, userLastActive]);

  // Debug: Monitor userTalkgroupAssignments changes (only log when it actually changes)
  useEffect(() => {
    if (userTalkgroupAssignments.size > 0) {
      console.log('[DispatcherConsole] userTalkgroupAssignments changed:', userTalkgroupAssignments);
    }
  }, [userTalkgroupAssignments]);

  const isChannelBusy = useCallback((talkgroupId?: string) => {
    const tg = talkgroupId || selectedTalkgroup;
    if (!tg) return false;
    if (tg === 'ALL_CALL') {
      for (const [, speakers] of activeTalkgroups) if ((speakers || []).length) return true;
      return false;
    }
    const speakers = activeTalkgroups.get(tg) || [];
    return speakers.length > 0;
  }, [selectedTalkgroup, activeTalkgroups]);

  const orderedTalkgroups = useMemo(() => {
    const arr = [...talkgroups] as Talkgroup[];
    arr.sort((a, b) => {
      const ap = pinnedTalkgroups.has(a.id) ? 0 : 1;
      const bp = pinnedTalkgroups.has(b.id) ? 0 : 1;
      if (ap !== bp) return ap - bp;
      if (a.id === 'PRIMARY') return -1;
      if (b.id === 'PRIMARY') return 1;
      return a.name.localeCompare(b.name);
    });
    return arr;
  }, [talkgroups, pinnedTalkgroups]);

  const handlePTTPress = () => {
    if (!selectedTalkgroup || isTransmitting || isPTTPressed) return;
    if (isChannelBusy() && !isPreemptMode) return;
    testAudioPlayback();
    setIsPTTPressed(true);
    startTransmitting(selectedTalkgroup);
  };

  const handlePTTRelease = () => {
    if (!isPTTPressed) return;
    setIsPTTPressed(false);
    stopTransmitting();
  };

  const toggleTalkgroupMute = useCallback((talkgroupId: string) => {
    console.log(`[DispatcherConsole] Toggling mute for ${talkgroupId}`);
    console.log(`[DispatcherConsole] mutedTalkgroups object:`, mutedTalkgroups);
    const isCurrentlyMuted = mutedTalkgroups?.has(talkgroupId) ?? false;
    console.log(`[DispatcherConsole] Currently muted: ${isCurrentlyMuted}, will ${isCurrentlyMuted ? 'unmute' : 'mute'}`);
    setTalkgroupMuted(talkgroupId, !isCurrentlyMuted);
  }, [setTalkgroupMuted, mutedTalkgroups]);

  const toggleTalkgroupPin = useCallback((talkgroupId: string) => {
    setPinnedTalkgroups((prev) => {
      const next = new Set(prev);
      next.has(talkgroupId) ? next.delete(talkgroupId) : next.add(talkgroupId);
      return next;
    });
  }, []);

  const updateVolume = (talkgroupId: string, volume: number) => {
    console.log(`[DispatcherConsole] Updating volume for ${talkgroupId} to ${volume}`);
    console.log(`[DispatcherConsole] Current muted state: ${mutedTalkgroups?.has(talkgroupId) ?? false}`);
    setTalkgroupVolume(talkgroupId, volume);
  };

  if (!user) {
    return <div className="dispatcher-console"><div className="loading">Connecting to radio system…</div></div>;
  }

  const centerName =
    talkgroups.find((tg: Talkgroup) => tg.id === selectedTalkgroup)?.name ||
    (selectedTalkgroup === 'ALL_CALL' ? 'ALL-CALL' : 'No Talkgroup');

  return (
    <div className="dispatcher-console">
      {/* Top Bar */}
      <header className="top-bar">
        <div className="brand">
          <div className="title">Dispatch Console</div>
          <div className="subtitle">{user.displayName} · {user.role?.toUpperCase?.()}</div>
        </div>

        <div className="bus">
          <div className={['conn', serverStatus].join(' ')}>
            <span className="led" />
            <span className="text">{serverStatus.toUpperCase()}</span>
          </div>
          <div className="net">
            <span>RTT {networkStatus.latency}ms</span>
            <span>Loss {networkStatus.packetLoss.toFixed(1)}%</span>
          </div>
          <div className="utc-time">
            <span>UTC {currentTime.toISOString().substr(11, 8)}</span>
          </div>
        </div>

        <div className="header-actions">
          <button
            className="settings-btn"
            onClick={() => setShowSettings(!showSettings)}
            title="Settings"
            aria-label="Open settings menu"
          >
            <Icon icon="material-symbols:settings" width="22" height="22" />
          </button>
        </div>
      </header>

      {/* Settings Popup */}
      {showSettings && (
        <div className="settings-overlay" onClick={() => setShowSettings(false)}>
          <div className="settings-popup" onClick={(e) => e.stopPropagation()}>
            <div className="settings-header">
              <h3>Settings</h3>
              <button className="close-btn" onClick={() => setShowSettings(false)}><Icon icon="material-symbols:close" width="20" height="20" /></button>
            </div>
            <div className="settings-content">
              <div className="settings-section">
                <h4>Audio Devices</h4>
                <div className="device-controls">
                  <label htmlFor="input-device">Input Device:</label>
                  <select 
                    id="input-device"
                    value={selectedInputDevice ?? ''} 
                    onChange={(e) => updateDevice('input', e.target.value)}
                  >
                    {(devices.filter((d: any) => d.kind === 'audioinput').length
                      ? devices.filter((d: any) => d.kind === 'audioinput')
                      : [{ deviceId: '', label: 'No inputs', kind: 'audioinput' as const }]
                    ).map((d: any) => <option key={d.deviceId} value={d.deviceId}>{d.label}</option>)}
                  </select>

                  <label htmlFor="output-device">Output Device:</label>
                  <select 
                    id="output-device"
                    value={selectedOutputDevice ?? ''} 
                    onChange={(e) => updateDevice('output', e.target.value)}
                  >
                    {(devices.filter((d: any) => d.kind === 'audiooutput').length
                      ? devices.filter((d: any) => d.kind === 'audiooutput')
                      : [{ deviceId: '', label: 'No outputs', kind: 'audiooutput' as const }]
                    ).map((d: any) => <option key={d.deviceId} value={d.deviceId}>{d.label}</option>)}
                  </select>
                </div>
              </div>

              <div className="settings-section">
                <h4>P25 Vocoder</h4>
                <div className="fx-controls">
                  <label className="fx-toggle">
                    <input
                      type="checkbox"
                      checked={txFxEnabled}
                      onChange={(e) => setTxFxEnabled(e.target.checked)}
                    />
                    <span>P25 {txFxEnabled ? 'ON' : 'OFF'}</span>
                  </label>

                  {txFxEnabled && (
                    <div className="p25-mode-control">
                      <label htmlFor="p25-mode">P25 Mode:</label>
                      <select
                        id="p25-mode"
                        className="p25-mode"
                        value={p25VocoderConfig.mode}
                        onChange={(e) => setP25VocoderMode(e.target.value as any)}
                      >
                        <option value="P25_PHASE1">Phase 1</option>
                        <option value="P25_PHASE2">Phase 2</option>
                        <option value="CUSTOM">Custom</option>
                      </select>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Grid layout: Left (cards), Center-Bottom (now playing), Right-Full-Height (PTT) */}
      <div className="main">
        {/* LEFT: Talkgroups (excluding All-Call) */}
        <aside className="left">
          <div className="left-scroll">
            <div className="tg-grid">
              {orderedTalkgroups.filter(tg => !tg.isAllCall).map((tg: Talkgroup) => {
                const isSelected = selectedTalkgroup === tg.id;
                const receiving = !!(activeTalkgroups.get(tg.id)?.length);
                const isBusy = isChannelBusy(tg.id);
                const hasConsumer = typeof hasConsumerForTalkgroup === 'function'
                  ? hasConsumerForTalkgroup(tg.id)
                  : receiving;

                return (
                  <TalkgroupTile
                    key={tg.id}
                    talkgroup={tg}
                    isActive={activeTalkgroups.has(tg.id)}
                    isSelected={isSelected}
                    isReceiving={receiving}
                    isBusy={isBusy}
                    onSelect={() => setSelectedTalkgroup(tg.id)}
                    onToggleMute={() => toggleTalkgroupMute(tg.id)}
                    onVolumeChange={(v) => updateVolume(tg.id, v)}
                    isMuted={mutedTalkgroups?.has(tg.id) ?? false}
                    volume={talkgroupVolumes?.get(tg.id) ?? 80}
                    isPinned={pinnedTalkgroups.has(tg.id)}
                    onTogglePin={() => toggleTalkgroupPin(tg.id)}
                    hasActiveConsumer={hasConsumer}
                    transmissionLogs={transmissionLogs.get(tg.id) || []}
                    playTransmissionAudio={playTransmissionAudio}
                    connectedUsers={connectedUsers}
                  />
                );
              })}
            </div>
          </div>
        </aside>



        {/* RIGHT: PTT & log (full height) */}
        <aside className="right">
          <div className="ptt">
            <button
              ref={pttButtonRef}
              className={[
                'ptt-btn',
                isTransmitting && 'tx',
                isPTTPressed && 'pressed',
                isChannelBusy() && !isPreemptMode && !isTransmitting && 'busy',
                isPreemptMode && 'preempt'
              ].filter(Boolean).join(' ')}
              disabled={(!selectedTalkgroup) || (isChannelBusy() && !isPreemptMode && !isTransmitting)}
              onMouseDown={handlePTTPress}
              onMouseUp={handlePTTRelease}
              onMouseLeave={() => isPTTPressed && handlePTTRelease()}
              onTouchStart={(e) => { e.preventDefault(); handlePTTPress(); }}
              onTouchEnd={(e) => { e.preventDefault(); handlePTTRelease(); }}
            >
              <div className="ptt-label">PTT</div>
              <div className="ptt-tg">{centerName}</div>
            </button>
            <button
              className={['preempt-btn', isPreemptMode && 'on'].filter(Boolean).join(' ')}
              onClick={() => setIsPreemptMode(!isPreemptMode)}
            >
              PREEMPT
            </button>
            <button
              className={['all-call-btn', selectedTalkgroup === 'ALL_CALL' && 'is-selected'].filter(Boolean).join(' ')}
              onClick={() => setSelectedTalkgroup('ALL_CALL')}
            >
              ALL CALL
            </button>
          </div>

          <div className="user-management">
            <div className="user-mgmt-header">
              <div className="user-mgmt-title">Online Users</div>
              <button 
                className="refresh-users-btn"
                onClick={refreshConnectedUsers}
                title="Refresh user list"
              >
                🔄
              </button>
            </div>
            <div className="user-list">
              {connectedUsers && Array.from(connectedUsers.values()).filter((userData: any) => userData.id).map((userData: any, index) => {
                const userTalkgroups = userTalkgroupAssignments?.get(userData.id) || [];
                const isDispatcher = userData.role === 'dispatcher';
                // For dispatchers, show "All Channels" since they can access all talkgroups
                // For units, show their primary assigned channel
                const primaryChannel = isDispatcher ? 'All Channels' : (userTalkgroups.length > 0 ? userTalkgroups[0] : 'None');
                const isOnline = true; // For now, all connected users are online
                const lastActiveTime = userLastActive.get(userData.id);
                const lastActive = lastActiveTime ? new Date(lastActiveTime).toLocaleTimeString() : 'Never';
                
                // Debug logging for channel changes (only log when channel actually changes)
                if (userData.id === 'user2') {
                  console.log(`[DispatcherConsole] Rendering user ${userData.id}:`, {
                    userTalkgroups,
                    primaryChannel,
                    userTalkgroupAssignments: userTalkgroupAssignments?.get(userData.id)
                  });
                }
                
                return (
                  <div key={userData.id || `user-${index}`} className={`user-item ${isDispatcher ? 'dispatcher' : 'unit'}`}>
                    <div className="user-header">
                      <div className="user-info">
                        <div className="user-name">{userData.displayName || userData.id}</div>
                        <div className={`user-role ${userData.role}`}>
                          {isDispatcher ? 'DISPATCHER' : (userData.claimedUnit || 'UNIT')}
                        </div>
                      </div>
                      <div className="user-status">
                        <div 
                          className={`status-indicator ${isOnline ? 'online' : 'offline'}`}
                          style={{
                            backgroundColor: isDispatcher 
                              ? '#32be5a' // Default green for dispatchers
                              : (userData.unitStatus ? getUnitStatusColor(userData.unitStatus) : '#a0a0a0')
                          }}
                        ></div>
                      </div>
                    </div>
                    
                    <div className="user-details">
                      <div className="channel-info">
                        <span className="channel-label">Channel:</span>
                        {isDispatcher ? (
                          <span className="channel-value">{primaryChannel}</span>
                        ) : (
                          <select
                            className="channel-selector"
                            value={primaryChannel === 'None' ? '' : primaryChannel}
                            onChange={(e) => {
                              console.log(`[DispatcherConsole] Channel selector changed for ${userData.id}:`, {
                                oldValue: primaryChannel,
                                newValue: e.target.value
                              });
                              if (e.target.value) {
                                moveUserToTalkgroup(userData.id, e.target.value);
                              }
                            }}
                          >
                            <option value="">Select Channel</option>
                            {talkgroups.map((tg: any) => (
                              <option key={tg.id} value={tg.id}>
                                {tg.name} ({tg.id})
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                      
                      {!isDispatcher && (
                        <div className="last-active">
                          <span className="active-label">Last active:</span>
                          <span className="active-time">{lastActive}</span>
                        </div>
                      )}
                      
                      {userTalkgroups.length > 1 && (
                        <div className="additional-channels">
                          <span className="additional-label">Also on:</span>
                          <div className="additional-list">
                            {userTalkgroups.slice(1).map((talkgroupId: string, tagIndex: number) => (
                              <span key={`${talkgroupId}-${tagIndex}`} className="additional-channel">
                                {talkgroupId}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              {(!connectedUsers || connectedUsers.size === 0) && (
                <div className="no-users">No users online</div>
              )}
            </div>
          </div>
        </aside>
      </div>

    </div>
  );
};

export default DispatcherConsole;
